import React, { FormEvent, useMemo, useState } from 'react';
import * as adminApi from '../../../../api/adminApi';
import { CreateAdminModal } from '../modals/CreateAdminModal';
import { ViewAdminModal } from '../modals/ViewAdminModal';
import { EditAdminModal } from '../modals/EditAdminModal';
import { RevokeAdminModal } from '../modals/RevokeAdminModal';
import styles from './AdminsSection.module.css';
import metricsStyles from '../statistics/MetricsCards.module.css';
import { Eyebrow, PageHeading } from '../../../../design-system';

export interface AdminsSectionProps {
  admins: adminApi.AdminSummary[];
  token: string | null;
  currentAdmin?: adminApi.AdminSummary | null;
  newAdmin: { email: string; name: string; password: string; customRoleId?: string };
  adminCreationStatus: 'idle' | 'success' | 'error';
  adminCreationError: string | null;
  credentialsStatus: 'idle' | 'loading' | 'success' | 'error';
  credentialsError: string | null;
  onInputChange: (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onAdminCreate: (event: FormEvent<HTMLFormElement>) => Promise<void>;
  onSendCredentials: () => Promise<void>;
  onAdminRefresh: () => void;
  onBack?: () => void;
  formatAdminText: (value?: string | null) => string;
  formatAdminDate: (value?: string | null) => string;
}

export const AdminsSection: React.FC<AdminsSectionProps> = ({
  admins,
  token,
  currentAdmin,
  newAdmin,
  adminCreationStatus,
  adminCreationError,
  credentialsStatus,
  credentialsError,
  onInputChange,
  onAdminCreate,
  onSendCredentials,
  onAdminRefresh,
  onBack,
  formatAdminText,
  formatAdminDate,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('Active');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [viewingAdmin, setViewingAdmin] = useState<adminApi.AdminSummary | null>(null);
  const [editingAdmin, setEditingAdmin] = useState<adminApi.AdminSummary | null>(null);
  const [revokingAdmin, setRevokingAdmin] = useState<adminApi.AdminSummary | null>(null);

  // Calculate KPIs
  const kpis = useMemo(() => {
    const total = admins.length;
    const active = admins.filter(a => a.is_active).length;
    const defaultAdmins = admins.filter(a => (a.role_name || 'Admin') === 'Admin').length;
    const customRoleAssignments = admins.filter(a => (a.role_name || 'Admin') !== 'Admin').length;

    return { total, active, defaultAdmins, customRoleAssignments };
  }, [admins]);

  const roleOptions = useMemo(() => {
    const names = new Set<string>(['Admin']);
    admins.forEach((admin) => names.add(admin.role_name || 'Admin'));
    return ['All', ...Array.from(names)];
  }, [admins]);

  // Filter admins
  const filteredAdmins = useMemo(() => {
    return admins.filter(admin => {
      const matchesSearch = admin.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           admin.email?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesRole = roleFilter === 'All' || (admin.role_name || 'Admin') === roleFilter;
      const matchesStatus = statusFilter === 'All' || 
                           (statusFilter === 'Active' && admin.is_active) ||
                           (statusFilter === 'Inactive' && !admin.is_active);
      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [admins, searchQuery, roleFilter, statusFilter]);

  const allowedActions = currentAdmin?.allowed_actions ?? [];
  const hasDefaultAdminAccess = !currentAdmin?.custom_role_id;
  const canCreateAdmins = hasDefaultAdminAccess || allowedActions.includes('create_user');
  const canEditAdmins = hasDefaultAdminAccess || allowedActions.includes('edit_permissions');
  const canRevokeAdmins = hasDefaultAdminAccess || allowedActions.includes('delete_user');

  // Handler functions
  const handleViewProfile = (admin: adminApi.AdminSummary) => {
    setViewingAdmin(admin);
  };

  const handleEditClick = (admin: adminApi.AdminSummary) => {
    setEditingAdmin(admin);
  };

  const handleEditSave = () => {
    // Close the edit modal after save
    setEditingAdmin(null);
    // Refresh admin list
    onAdminRefresh();
  };

  const handleRevokeClick = (admin: adminApi.AdminSummary) => {
    setRevokingAdmin(admin);
  };

  const handleConfirmRevoke = async () => {
    if (revokingAdmin) {
      // Close the revoke modal and refresh list
      setRevokingAdmin(null);
      // Refresh admin list
      onAdminRefresh();
    }
  };

  const handleStatusChange = (adminId: string, isActive: boolean) => {
    // Status change is already handled by the modal's API call
    // Just refresh the admin list
    onAdminRefresh();
  };

  return (
    <section className={styles.section}>
      {/* Header */}
      <div className={styles.header}>
        {onBack && (
          <button type="button" className={styles.backButton} onClick={onBack}>
            ← Users
          </button>
        )}
        <div>
          <Eyebrow tone="default" dot>ADMINS</Eyebrow>
          <PageHeading
            accentWord="Management"
            accentTone="orange"
            size="h2"
            align="left"
            level="h2"
            className={styles.title}
          >
            Admin Management
          </PageHeading>
        </div>
      </div>

      {/* Search and Filters */}
      <div className={styles.toolbar}>
        <div className={styles.searchWrap}>
          <input
            type="text"
            placeholder="Search admins..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        <div className={styles.selectWrap}>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className={styles.select}
          >
            <option value="All">Role: All</option>
            {roleOptions.filter((role) => role !== 'All').map((role) => (
              <option key={role} value={role}>{role}</option>
            ))}
          </select>
        </div>
        <div className={styles.selectWrap}>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={styles.select}
          >
            <option>Status: Active</option>
            <option value="All">Status: All</option>
            <option value="Inactive">Status: Inactive</option>
          </select>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className={styles.primaryButton}
          disabled={!canCreateAdmins}
          title={!canCreateAdmins ? 'Your assigned role does not allow creating admins.' : undefined}
        >
          + Add New Admin
        </button>
      </div>

      {/* KPI Cards */}
      <div className={styles.kpiGrid}>
        <div className={`${metricsStyles.metricCard} ${metricsStyles.metric_gold}`}>
          <span className={metricsStyles.metricIconBadge}>AD</span>
          <div className={metricsStyles.metricContent}>
            <div className={metricsStyles.metricLabel}>Total Admins</div>
            <div className={metricsStyles.metricValue}>{kpis.total}</div>
            <div className={metricsStyles.metricMeta}>All admin users</div>
          </div>
        </div>

        <div className={`${metricsStyles.metricCard} ${metricsStyles.metric_green}`}>
          <span className={metricsStyles.metricIconBadge}>●</span>
          <div className={metricsStyles.metricContent}>
            <div className={metricsStyles.metricLabel}>Active Now</div>
            <div className={metricsStyles.metricValue}>{kpis.active}</div>
            <div className={metricsStyles.metricMeta}>Currently active</div>
          </div>
        </div>

        <div className={`${metricsStyles.metricCard} ${metricsStyles.metric_blue}`}>
          <span className={metricsStyles.metricIconBadge}>A</span>
          <div className={metricsStyles.metricContent}>
            <div className={metricsStyles.metricLabel}>Default Admins</div>
            <div className={metricsStyles.metricValue}>{kpis.defaultAdmins}</div>
            <div className={metricsStyles.metricMeta}>Using full default access</div>
          </div>
        </div>

        <div className={`${metricsStyles.metricCard} ${metricsStyles.metric_pink}`}>
          <span className={metricsStyles.metricIconBadge}>◎</span>
          <div className={metricsStyles.metricContent}>
            <div className={metricsStyles.metricLabel}>Custom Roles</div>
            <div className={metricsStyles.metricValue}>{kpis.customRoleAssignments}</div>
            <div className={metricsStyles.metricMeta}>Assigned saved role templates</div>
          </div>
        </div>
      </div>

      {/* Create Admin Modal */}
      <CreateAdminModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        token={token}
        newAdmin={newAdmin}
        onInputChange={onInputChange}
        onAdminCreate={onAdminCreate}
        onSendCredentials={onSendCredentials}
        adminCreationStatus={adminCreationStatus}
        adminCreationError={adminCreationError}
        credentialsStatus={credentialsStatus}
        credentialsError={credentialsError}
      />

      {/* Admin Cards Grid */}
      <div className={styles.cardsGrid}>
        {filteredAdmins.map(admin => (
          <div key={admin.id} className={styles.adminCard}>
            <div className={styles.cardHeader}>
              <div className={styles.avatar}>{admin.full_name?.charAt(0).toUpperCase() || 'A'}</div>
              <div style={{ flex: 1 }}>
                <h4 className={styles.name}>
                  {formatAdminText(admin.full_name)}
                  <span className={styles.roleBadge}>{admin.role_name || 'Admin'}</span>
                </h4>
                <p className={styles.email}>
                  {admin.email}
                </p>
              </div>
            </div>
            <div className={styles.statusBox}>
              <p className={styles.metaText}>
                Status: <span className={admin.is_active ? styles.statusActive : styles.statusInactive}>
                  {admin.is_active ? '● Active' : '● Inactive'}
                </span>
              </p>
              <p className={styles.metaText}>
                Last login: <span style={{ fontWeight: 600 }}>{admin.last_login_at ? formatAdminDate(admin.last_login_at) : 'Never'}</span>
              </p>

            </div>
            <div className={styles.actions}>
              <button onClick={() => handleViewProfile(admin)} className={styles.actionButton}>View Profile</button>
              <button onClick={() => handleEditClick(admin)} className={styles.actionButton} disabled={!canEditAdmins}>Edit</button>
              {admin.id !== currentAdmin?.id && (
                <button onClick={() => handleRevokeClick(admin)} className={`${styles.actionButton} ${styles.dangerButton}`} disabled={!canRevokeAdmins}>
                  {admin.is_active ? 'Revoke' : 'Remove'}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredAdmins.length === 0 && (
        <div className={styles.emptyState}>
          <p>No admins found matching your filters.</p>
        </div>
      )}

      {/* View Admin Modal */}
      <ViewAdminModal
        isOpen={viewingAdmin !== null}
        onClose={() => setViewingAdmin(null)}
        token={token}
        admin={viewingAdmin}
        formatAdminDate={formatAdminDate}
        onEdit={() => handleEditClick(viewingAdmin!)}
        onRevoke={() => handleRevokeClick(viewingAdmin!)}
      />

      {/* Edit Admin Modal */}
      <EditAdminModal
        isOpen={editingAdmin !== null}
        onClose={() => setEditingAdmin(null)}
        token={token}
        admin={editingAdmin}
        currentAdminId={currentAdmin?.id ?? null}
        onSave={handleEditSave}
        onStatusChange={handleStatusChange}
      />

      {/* Revoke Admin Modal */}
      <RevokeAdminModal
        isOpen={revokingAdmin !== null}
        onClose={() => setRevokingAdmin(null)}
        token={token}
        admin={revokingAdmin}
        onConfirmRevoke={handleConfirmRevoke}
      />
    </section>
  );
};
